import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  LayoutTemplate,
  Library,
  Link2,
  LogOut,
  Maximize2,
  Plus,
  RefreshCw,
  Save,
  Send,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Wand2,
  X,
  type LucideIcon
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ApiClient, resolveImageUrl } from "./api";
import type { Card, DraftPayload, NewProjectForm, Project, ProjectBundle, Template, User, Workspace } from "./types";

type WorkflowStep = "input" | "settings" | "review" | "images";

type StepMeta = {
  id: WorkflowStep;
  label: string;
  detail: string;
  icon: LucideIcon;
};

const workflowSteps: StepMeta[] = [
  { id: "input", label: "输入", detail: "提示文本", icon: FileText },
  { id: "settings", label: "参数", detail: "样式模板", icon: SlidersHorizontal },
  { id: "review", label: "确认", detail: "内容描述", icon: CheckCircle2 },
  { id: "images", label: "图片", detail: "预览导出", icon: ImageIcon }
];

const emptyForm: NewProjectForm = {
  title: "",
  brand_name: "扬子教育",
  source_type: "topic",
  source_text: "",
  source_url: "",
  industry: "education",
  content_goal: "lead_generation",
  tone: "friendly",
  visual_style: "cartoon",
  aspect_ratio: "9:16",
  cta_hint: "发站内信咨询课程方案",
  card_count: 5
};

const industryOptions = [
  ["education", "教育"],
  ["ecommerce", "电商"],
  ["restaurant", "餐饮"],
  ["local_service", "本地生活"],
  ["other", "其他"]
] as const;

const goalOptions = [
  ["lead_generation", "获客咨询"],
  ["product_seeding", "种草推荐"],
  ["event_promotion", "活动推广"],
  ["brand_awareness", "品牌认知"],
  ["conversion", "转化成交"]
] as const;

const toneOptions = [
  ["friendly", "自然亲切"],
  ["professional", "专业可信"],
  ["viral", "种草传播"],
  ["premium", "高级简洁"],
  ["storytelling", "故事化"]
] as const;

const visualOptions = [
  ["cartoon", "卡通插画"],
  ["clean_poster", "清爽海报"],
  ["lifestyle", "生活方式"],
  ["flat_illustration", "扁平插画"],
  ["realistic", "真实质感"]
] as const;

const aspectOptions = [
  ["9:16", "9:16 竖图", "小红书图文"],
  ["1:1", "1:1 方图", "封面与头像"],
  ["16:9", "16:9 横图", "视频封面"]
] as const;

function App() {
  const [token, setToken] = useState(() => localStorage.getItem("picnote_token"));
  const api = useMemo(() => new ApiClient(token), [token]);
  const [user, setUser] = useState<User | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [bundle, setBundle] = useState<ProjectBundle | null>(null);
  const [draft, setDraft] = useState<DraftPayload | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [form, setForm] = useState<NewProjectForm>(emptyForm);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [activeStep, setActiveStep] = useState<WorkflowStep>("input");
  const [previewCard, setPreviewCard] = useState<Card | null>(null);
  const [loginEmail, setLoginEmail] = useState("operator@yangzi.example");
  const [loginName, setLoginName] = useState("扬子教育运营");
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [manualCopy, setManualCopy] = useState<string | null>(null);

  const activeIndex = workflowSteps.findIndex((step) => step.id === activeStep);
  const completedImages = cards.filter((card) => card.status === "completed" && card.image_url);
  const currentAspectRatio = bundle?.project.aspect_ratio || form.aspect_ratio;

  useEffect(() => {
    api.setToken(token);
  }, [api, token]);

  useEffect(() => {
    if (!token) return;
    void bootstrap();
  }, [token]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [activeStep]);

  async function bootstrap() {
    try {
      const me = await api.me();
      setUser(me.user);
      setWorkspace(me.workspace);
      await Promise.all([loadProjects(), loadTemplates()]);
    } catch (error) {
      localStorage.removeItem("picnote_token");
      setToken(null);
      setUser(null);
      setWorkspace(null);
      showError(error);
    }
  }

  async function loadProjects() {
    const result = await api.listProjects();
    setProjects(result.projects);
  }

  async function loadTemplates() {
    const result = await api.listTemplates();
    setTemplates(result.templates);
  }

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    await run("登录中", async () => {
      const result = await api.login(loginEmail, loginName);
      localStorage.setItem("picnote_token", result.token);
      setToken(result.token);
      setUser(result.user);
      setWorkspace(result.workspace);
      setToast("已进入工作台");
    });
  }

  async function logout() {
    await run("退出中", async () => {
      await api.logout().catch(() => undefined);
      localStorage.removeItem("picnote_token");
      setToken(null);
      setUser(null);
      setWorkspace(null);
      setBundle(null);
      setDraft(null);
      setCards([]);
      setActiveStep("input");
    });
  }

  async function createAndGenerate(event?: React.FormEvent) {
    event?.preventDefault();
    await run("生成内容描述", async () => {
      const sourceText = form.source_text.trim();
      let nextForm: NewProjectForm = {
        ...form,
        title: form.title.trim(),
        brand_name: form.brand_name.trim() || "扬子教育",
        source_text: sourceText,
        source_url: form.source_url.trim()
      };

      if (nextForm.source_type === "url") {
        if (!nextForm.source_url) {
          setToast("请先填写链接");
          return;
        }
        const fetched = await api.fetchUrl(nextForm.source_url);
        if (fetched.success) {
          nextForm = {
            ...nextForm,
            source_text: fetched.text,
            title: nextForm.title || fetched.title
          };
        }
      } else if (!nextForm.source_text) {
        nextForm = {
          ...nextForm,
          source_text: `${nextForm.brand_name}课程宣传图文，适合小红书传播，结尾引导用户发站内信咨询。`
        };
      }

      const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);
      if (selectedTemplate) {
        nextForm = {
          ...nextForm,
          source_text: `${nextForm.source_text}\n\n模板偏好：${selectedTemplate.name}${
            selectedTemplate.description ? `。${selectedTemplate.description}` : ""
          }`
        };
      }

      const created = await api.createProject(nextForm);
      const generated = await api.generateCopy(created.project.id);
      const nextBundle = await api.getProject(created.project.id);
      setBundle(nextBundle);
      setDraft(generated.draft);
      setCards(generated.cards);
      setActiveStep("review");
      await loadProjects();
      setToast("内容描述已生成");
    });
  }

  async function openProject(projectId: string) {
    await run("读取项目", async () => {
      const nextBundle = await api.getProject(projectId);
      setBundle(nextBundle);
      setDraft(nextBundle.draft);
      setCards(nextBundle.cards);
      setForm(formFromProject(nextBundle.project));
      setManualCopy(null);
      setActiveStep(nextBundle.cards.some((card) => card.image_url) ? "images" : nextBundle.draft ? "review" : "input");
    });
  }

  async function persistCurrent(): Promise<ProjectBundle | null> {
    if (!bundle) return null;
    if (draft) {
      const saved = await api.patchDraft(draft.id, draft);
      setDraft(saved.draft);
    }
    await Promise.all(
      cards.map((card) =>
        api.patchCard(card.id, {
          card_index: card.card_index,
          card_role: card.card_role,
          headline: card.headline,
          sub_text: card.sub_text,
          visual_description: card.visual_description,
          image_prompt: card.image_prompt
        })
      )
    );
    const latest = await api.getProject(bundle.project.id);
    setBundle(latest);
    setCards(latest.cards);
    return latest;
  }

  async function saveCurrent() {
    await run("保存草稿", async () => {
      await persistCurrent();
      setToast("草稿已保存");
    });
  }

  async function generateImages() {
    if (!bundle) return;
    await run("生成图片", async () => {
      const latest = await persistCurrent();
      const projectId = latest?.project.id || bundle.project.id;
      await api.generateImages(projectId, cards.map((card) => card.id));
      setActiveStep("images");
      await pollStatus(projectId);
      setToast("图片生成任务已完成");
    });
  }

  async function retryCard(cardId: string) {
    await run("重新生成", async () => {
      await api.retryCard(cardId);
      setActiveStep("images");
      if (bundle) await pollStatus(bundle.project.id);
    });
  }

  async function pollStatus(projectId: string) {
    for (let attempt = 0; attempt < 24; attempt += 1) {
      const status = await api.generationStatus(projectId);
      setBundle(status);
      setDraft(status.draft);
      setCards(status.cards);
      const unfinished = status.cards.some((card) => ["queued", "running", "pending"].includes(card.status));
      if (!unfinished) return;
      await new Promise((resolve) => window.setTimeout(resolve, 1500));
    }
  }

  async function downloadZip() {
    if (!bundle) return;
    await run("下载图片", async () => {
      const blob = await api.imageZip(bundle.project.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${bundle.project.title || "picnote"}_images.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      setToast("图片 ZIP 已开始下载");
    });
  }

  async function copyPublishText() {
    if (!bundle) return;
    await run("复制文案", async () => {
      const result = await api.copyText(bundle.project.id);
      try {
        await navigator.clipboard.writeText(result.copy_text);
        setToast("发布文案已复制");
      } catch {
        setManualCopy(result.copy_text);
        setToast("请使用手动复制面板");
      }
    });
  }

  async function duplicateCard(cardId: string) {
    await run("复制图卡", async () => {
      const result = await api.duplicateCard(cardId);
      setCards(result.cards);
    });
  }

  async function deleteCard(cardId: string) {
    await run("删除图卡", async () => {
      const result = await api.deleteCard(cardId);
      setCards(result.cards);
    });
  }

  async function moveCard(cardId: string, direction: -1 | 1) {
    if (!bundle) return;
    const index = cards.findIndex((card) => card.id === cardId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= cards.length) return;
    const next = [...cards];
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item);
    const normalized = next.map((card, nextIndex) => ({ ...card, card_index: nextIndex + 1 }));
    setCards(normalized);
    const result = await api.reorderCards(bundle.project.id, normalized.map((card) => card.id));
    setCards(result.cards);
  }

  function updateDraft<K extends keyof DraftPayload>(key: K, value: DraftPayload[K]) {
    if (!draft) return;
    setDraft({ ...draft, [key]: value });
  }

  function updateCard(cardId: string, patch: Partial<Card>) {
    setCards((current) => current.map((card) => (card.id === cardId ? { ...card, ...patch } : card)));
  }

  async function run(label: string, action: () => Promise<void>) {
    setBusy(label);
    setToast(null);
    try {
      await action();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(null);
    }
  }

  function showError(error: unknown) {
    setToast(error instanceof Error ? error.message : "操作失败");
  }

  function canOpenStep(step: WorkflowStep): boolean {
    if (step === "input" || step === "settings") return true;
    if (step === "review") return Boolean(draft);
    return cards.length > 0;
  }

  function goToStep(step: WorkflowStep) {
    if (canOpenStep(step)) {
      setActiveStep(step);
      return;
    }
    setToast(step === "review" ? "请先生成内容描述" : "请先确认内容描述");
  }

  function goPrevious() {
    const nextStep = workflowSteps[Math.max(activeIndex - 1, 0)]?.id;
    if (nextStep) goToStep(nextStep);
  }

  async function runPrimaryAction() {
    if (activeStep === "input") {
      setActiveStep("settings");
      return;
    }
    if (activeStep === "settings") {
      await createAndGenerate();
      return;
    }
    if (activeStep === "review") {
      await generateImages();
      return;
    }
    if (completedImages.length) {
      await downloadZip();
    }
  }

  function primaryActionLabel() {
    if (activeStep === "input") return "下一步";
    if (activeStep === "settings") return "生成内容";
    if (activeStep === "review") return "生成图片";
    return "下载图片";
  }

  function primaryActionDisabled() {
    if (Boolean(busy)) return true;
    if (activeStep === "review") return !cards.length;
    if (activeStep === "images") return !completedImages.length;
    return false;
  }

  if (!token || !user) {
    return (
      <main className="login-shell">
        <section className="login-panel">
          <div className="brand-mark">PicNote</div>
          <h1>图记工作台</h1>
          <form onSubmit={handleLogin} className="login-form">
            <label>
              邮箱
              <input value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} type="email" required />
            </label>
            <label>
              姓名
              <input value={loginName} onChange={(event) => setLoginName(event.target.value)} />
            </label>
            <button type="submit" className="primary-button" disabled={Boolean(busy)}>
              <Send size={16} />
              进入
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <div className="brand-mark compact">PicNote</div>
          <p>{workspace?.name}</p>
        </div>
        <div className="topbar-actions">
          <span>{user.name || user.email}</span>
          <button type="button" className="icon-button" onClick={logout} aria-label="退出">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <section className="workflow-layout">
        <aside className="step-sidebar">
          <nav className="step-nav" aria-label="工作流步骤">
            {workflowSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <button
                  key={step.id}
                  type="button"
                  className={step.id === activeStep ? "step-tab active" : "step-tab"}
                  onClick={() => goToStep(step.id)}
                  disabled={!canOpenStep(step.id)}
                >
                  <span className="step-number">{String(index + 1).padStart(2, "0")}</span>
                  <Icon size={16} />
                  <span>
                    {step.label}
                    <small>{step.detail}</small>
                  </span>
                </button>
              );
            })}
          </nav>

          <section className="history-block">
            <div className="section-title">
              <Library size={16} />
              历史项目
            </div>
            <div className="project-list">
              {projects.map((project) => (
                <button
                  type="button"
                  key={project.id}
                  className={bundle?.project.id === project.id ? "project-row active" : "project-row"}
                  onClick={() => openProject(project.id)}
                >
                  <span>{project.title || project.brand_name || "未命名项目"}</span>
                  <small>{project.status}</small>
                </button>
              ))}
              {!projects.length ? <p className="muted">暂无项目</p> : null}
            </div>
          </section>
        </aside>

        <section className="step-stage">
          {activeStep === "input" ? renderInputStep() : null}
          {activeStep === "settings" ? renderSettingsStep() : null}
          {activeStep === "review" ? renderReviewStep() : null}
          {activeStep === "images" ? renderImagesStep() : null}
        </section>
      </section>

      {manualCopy ? (
        <section className="manual-copy">
          <button type="button" className="icon-button small" onClick={() => setManualCopy(null)} aria-label="关闭">
            <X size={14} />
          </button>
          <textarea value={manualCopy} readOnly rows={8} />
        </section>
      ) : null}

      {previewCard?.image_url ? (
        <section className="image-lightbox" role="dialog" aria-modal="true" onClick={() => setPreviewCard(null)}>
          <div className="lightbox-panel" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="icon-button" onClick={() => setPreviewCard(null)} aria-label="关闭大图">
              <X size={16} />
            </button>
            <img src={resolveImageUrl(previewCard.image_url)} alt={previewCard.headline || "图片预览"} />
            <p>{previewCard.headline || "图片预览"}</p>
          </div>
        </section>
      ) : null}

      <nav className="bottom-bar workflow-bottom">
        <button type="button" onClick={goPrevious} disabled={activeIndex === 0 || Boolean(busy)}>
          <ArrowLeft size={16} />
          上一步
        </button>
        <button type="button" className="primary-bottom" onClick={() => void runPrimaryAction()} disabled={primaryActionDisabled()}>
          {activeStep === "images" ? <Download size={16} /> : <ArrowRight size={16} />}
          {primaryActionLabel()}
        </button>
        <button type="button" onClick={copyPublishText} disabled={!draft || Boolean(busy)}>
          <Copy size={16} />
          复制
        </button>
      </nav>

      {busy ? <div className="busy-toast">{busy}...</div> : null}
      {toast ? <div className="toast">{toast}</div> : null}
    </main>
  );

  function renderInputStep() {
    return (
      <form className="workflow-panel input-panel" onSubmit={(event) => void createAndGenerate(event)}>
        <PanelHead icon={Wand2} step="步骤 1" title="输入提示文本" />
        <div className="source-switch segmented">
          {(["topic", "text", "url"] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={form.source_type === type ? "active" : ""}
              onClick={() => setForm({ ...form, source_type: type })}
            >
              {type === "topic" ? "主题" : type === "text" ? "文案" : "链接"}
            </button>
          ))}
        </div>
        <div className="field-pair">
          <label>
            品牌
            <input value={form.brand_name} onChange={(event) => setForm({ ...form, brand_name: event.target.value })} />
          </label>
          <label>
            标题
            <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} placeholder="可留空" />
          </label>
        </div>
        {form.source_type === "url" ? (
          <label>
            链接
            <div className="input-with-icon">
              <Link2 size={15} />
              <input
                value={form.source_url}
                onChange={(event) => setForm({ ...form, source_url: event.target.value })}
                placeholder="https://..."
                required
              />
            </div>
          </label>
        ) : null}
        <label>
          {form.source_type === "text" ? "参考文案" : "提示文本"}
          <textarea
            value={form.source_text}
            onChange={(event) => setForm({ ...form, source_text: event.target.value })}
            rows={8}
            placeholder="可留空。也可以写课程卖点、目标人群、推广重点。"
          />
        </label>
        <div className="panel-actions">
          <button type="button" className="primary-button" onClick={() => setActiveStep("settings")}>
            <ArrowRight size={16} />
            选择参数
          </button>
        </div>
      </form>
    );
  }

  function renderSettingsStep() {
    return (
      <section className="workflow-panel">
        <PanelHead icon={SlidersHorizontal} step="步骤 2" title="选择样式和参数" />
        <div className="settings-grid">
          <Select label="目标" value={form.content_goal} options={goalOptions} onChange={(content_goal) => setForm({ ...form, content_goal })} />
          <Select label="语气" value={form.tone} options={toneOptions} onChange={(tone) => setForm({ ...form, tone })} />
          <Select label="行业" value={form.industry} options={industryOptions} onChange={(industry) => setForm({ ...form, industry })} />
          <label>
            模板
            <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
              <option value="">自动选择</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="choice-section">
          <div className="section-title">
            <LayoutTemplate size={16} />
            图片风格
          </div>
          <div className="option-grid">
            {visualOptions.map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={form.visual_style === value ? "option-tile active" : "option-tile"}
                onClick={() => setForm({ ...form, visual_style: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="choice-section">
          <div className="section-title">
            <Maximize2 size={16} />
            长宽比
          </div>
          <div className="ratio-grid">
            {aspectOptions.map(([value, label, detail]) => (
              <button
                type="button"
                key={value}
                className={form.aspect_ratio === value ? "ratio-tile active" : "ratio-tile"}
                onClick={() => setForm({ ...form, aspect_ratio: value })}
              >
                <span className={`ratio-box ${ratioClass(value)}`} />
                <span>
                  {label}
                  <small>{detail}</small>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="field-pair">
          <label>
            图片张数
            <input
              type="range"
              min={3}
              max={8}
              value={form.card_count}
              onChange={(event) => setForm({ ...form, card_count: Number(event.target.value) })}
            />
            <span className="range-value">{form.card_count} 张</span>
          </label>
          <label>
            CTA
            <input value={form.cta_hint} onChange={(event) => setForm({ ...form, cta_hint: event.target.value })} />
          </label>
        </div>

        <div className="panel-actions split">
          <button type="button" className="ghost-button" onClick={() => setActiveStep("input")}>
            <ArrowLeft size={16} />
            返回输入
          </button>
          <button type="button" className="primary-button" onClick={() => void createAndGenerate()} disabled={Boolean(busy)}>
            <Sparkles size={16} />
            生成内容描述
          </button>
        </div>
      </section>
    );
  }

  function renderReviewStep() {
    if (!draft || !bundle) {
      return (
        <section className="workflow-panel empty-state">
          <Sparkles size={26} />
          <h2>暂无内容描述</h2>
          <button type="button" className="primary-button narrow" onClick={() => setActiveStep("settings")}>
            选择参数
          </button>
        </section>
      );
    }

    return (
      <section className="workflow-panel review-panel">
        <PanelHead icon={CheckCircle2} step="步骤 3" title="确认每张图的内容描述" />
        <div className="draft-summary">
          <div>
            <p className="eyebrow">{bundle.project.brand_name || "PicNote"}</p>
            <input
              className="title-input"
              value={draft.selected_title}
              onChange={(event) => updateDraft("selected_title", event.target.value)}
            />
          </div>
          <StatusPill status={draft.compliance_status} />
        </div>

        {draft.compliance_notes.length ? (
          <div className="risk-strip">
            {draft.compliance_notes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </div>
        ) : null}

        <details className="copy-detail">
          <summary>发布文案和 Tag</summary>
          <label>
            正文
            <textarea value={draft.body} onChange={(event) => updateDraft("body", event.target.value)} rows={7} />
          </label>
          <label>
            CTA
            <input value={draft.cta} onChange={(event) => updateDraft("cta", event.target.value)} />
          </label>
          <label>
            Tag
            <textarea
              value={draft.tags.join(" ")}
              onChange={(event) =>
                updateDraft(
                  "tags",
                  event.target.value
                    .split(/\s+/)
                    .map((tag) => tag.replace(/^#+/, "").trim())
                    .filter(Boolean)
                )
              }
              rows={2}
            />
          </label>
        </details>

        <div className="description-list">
          {cards.map((card, index) => (
            <article key={card.id} className="content-card">
              <div className="content-card-head">
                <span>
                  {String(card.card_index).padStart(2, "0")} / {card.card_role || "card"}
                </span>
                <div className="mini-actions">
                  <button type="button" className="icon-button small" onClick={() => moveCard(card.id, -1)} disabled={index === 0} aria-label="上移">
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    className="icon-button small"
                    onClick={() => moveCard(card.id, 1)}
                    disabled={index === cards.length - 1}
                    aria-label="下移"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button type="button" className="icon-button small" onClick={() => duplicateCard(card.id)} aria-label="复制图卡">
                    <Plus size={14} />
                  </button>
                  <button type="button" className="icon-button small danger" onClick={() => deleteCard(card.id)} aria-label="删除图卡">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="field-pair">
                <label>
                  图上标题
                  <input value={card.headline || ""} onChange={(event) => updateCard(card.id, { headline: event.target.value })} />
                </label>
                <label>
                  副文案
                  <input value={card.sub_text || ""} onChange={(event) => updateCard(card.id, { sub_text: event.target.value })} />
                </label>
              </div>
              <label>
                内容描述
                <textarea
                  value={card.visual_description || ""}
                  onChange={(event) => updateCard(card.id, { visual_description: event.target.value })}
                  rows={3}
                />
              </label>
              <details className="prompt-detail">
                <summary>隐藏 Prompt</summary>
                <textarea
                  value={card.image_prompt || ""}
                  onChange={(event) => updateCard(card.id, { image_prompt: event.target.value })}
                  rows={4}
                />
              </details>
            </article>
          ))}
        </div>

        <div className="panel-actions split sticky-actions">
          <button type="button" className="ghost-button" onClick={() => void saveCurrent()} disabled={Boolean(busy)}>
            <Save size={16} />
            保存
          </button>
          <button type="button" className="primary-button" onClick={() => void generateImages()} disabled={!cards.length || Boolean(busy)}>
            <ImageIcon size={16} />
            确认并生成图片
          </button>
        </div>
      </section>
    );
  }

  function renderImagesStep() {
    return (
      <section className="workflow-panel image-panel">
        <PanelHead icon={ImageIcon} step="步骤 4" title="查看图片预览" />
        <div className="image-meta">
          <span>{cards.length} 张</span>
          <span>{currentAspectRatio}</span>
          <span>{completedImages.length} 已完成</span>
        </div>
        <div className="thumbnail-grid">
          {cards.map((card) => (
            <article key={card.id} className="thumbnail-card">
              <button
                type="button"
                className={`thumbnail-frame ${ratioClass(currentAspectRatio)}`}
                onClick={() => card.image_url && setPreviewCard(card)}
                disabled={!card.image_url}
              >
                {card.image_url ? (
                  <>
                    <img src={resolveImageUrl(card.image_url)} alt={card.headline || "图卡预览"} />
                    <span className="view-badge">
                      <Eye size={14} />
                      全屏
                    </span>
                  </>
                ) : (
                  <span>{card.status}</span>
                )}
              </button>
              <div className="thumbnail-info">
                <span>{card.headline || `图 ${card.card_index}`}</span>
                <StatusPill status={card.status} />
              </div>
              <button type="button" className="ghost-button compact" onClick={() => retryCard(card.id)} disabled={Boolean(busy)}>
                <RefreshCw size={14} />
                重新生成
              </button>
            </article>
          ))}
          {!cards.length ? (
            <div className="empty-state">
              <ImageIcon size={26} />
              <h2>暂无图片</h2>
            </div>
          ) : null}
        </div>
        <div className="panel-actions split sticky-actions">
          <button type="button" className="ghost-button" onClick={copyPublishText} disabled={!draft || Boolean(busy)}>
            <Copy size={16} />
            复制文案
          </button>
          <button type="button" className="primary-button" onClick={downloadZip} disabled={!completedImages.length || Boolean(busy)}>
            <Download size={16} />
            下载图片
          </button>
        </div>
      </section>
    );
  }
}

function PanelHead({ icon: Icon, step, title }: { icon: LucideIcon; step: string; title: string }) {
  return (
    <div className="panel-head">
      <span className="panel-step">{step}</span>
      <h2>
        <Icon size={18} />
        {title}
      </h2>
    </div>
  );
}

function Select<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: readonly (readonly [T, string])[];
  onChange: (value: T) => void;
}) {
  return (
    <label>
      {label}
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map(([optionValue, text]) => (
          <option key={optionValue} value={optionValue}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusPill({ status }: { status: string }) {
  return <span className={`status-pill status-${status}`}>{status}</span>;
}

function formFromProject(project: Project): NewProjectForm {
  return {
    title: project.title || "",
    brand_name: project.brand_name || "扬子教育",
    source_type: project.source_type,
    source_text: project.source_text || "",
    source_url: project.source_url || "",
    industry: project.industry,
    content_goal: project.content_goal,
    tone: project.tone,
    visual_style: project.visual_style,
    aspect_ratio: project.aspect_ratio || "9:16",
    cta_hint: project.cta_hint || "发站内信咨询课程方案",
    card_count: project.card_count || 5
  };
}

function ratioClass(value: string): string {
  return `ratio-${value.replace(":", "-")}`;
}

export default App;
